<script lang="ts">
  import { defineComponent } from 'vue'
  import { Check, CircleAlert, X } from 'lucide-vue'
  import { useToast } from '@/composables/useToast'

  export default defineComponent({
    name: 'ToastContainer',

    components: { IconAlert: CircleAlert, IconCheck: Check, IconX: X },

    setup() {
      const { toasts, dismiss, pause, resume } = useToast()

      return { toasts, dismiss, pause, resume }
    },
  })
</script>

<template>
  <div class="toasts" aria-live="polite">
    <TransitionGroup name="toast" tag="ul">
      <li
        v-for="item in toasts"
        :key="item.id"
        class="toast"
        :class="`toast--${item.tone}`"
        @mouseenter="pause(item.id)"
        @mouseleave="resume(item.id)"
        @focusin="pause(item.id)"
        @focusout="resume(item.id)"
      >
        <IconAlert v-if="item.tone === 'error'" class="toast__icon" />
        <IconCheck v-else class="toast__icon" />

        <span class="toast__message">{{ $t(item.message, item.params) }}</span>

        <button
          type="button"
          class="toast__close"
          :aria-label="String($t('toast.dismiss'))"
          @click="dismiss(item.id)"
        >
          <IconX />
        </button>
      </li>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
  @use '@/variables' as *;

  .toasts {
    position: fixed;
    right: $space-5;
    bottom: $space-5;
    z-index: 90000;
    pointer-events: none;

    @media (max-width: $breakpoint) {
      right: $space-4;
      left: $space-4;
      bottom: $space-4;
    }

    > ul {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: $space-2;
      margin: 0;
      padding: 0;
      list-style: none;
    }
  }

  .toast {
    display: flex;
    align-items: center;
    gap: $space-3;
    width: 22rem;
    max-width: 100%;
    padding: $space-3 $space-3 $space-3 $space-4;
    border-left: 4px solid;
    border-radius: $radius-md;
    background-color: $surface;
    box-shadow: $shadow-md;
    color: $text-color;
    font-size: $font-size-sm;
    pointer-events: auto;

    &--success {
      border-left-color: $color-success;

      .toast__icon {
        color: darken($color-success, 10%);
      }
    }

    &--error {
      border-left-color: $color-danger;

      .toast__icon {
        color: $color-danger;
      }
    }

    &__icon {
      flex-shrink: 0;
    }

    &__message {
      flex-grow: 1;
    }

    &__close {
      display: flex;
      flex-shrink: 0;
      padding: $space-1;
      border: 0;
      border-radius: $radius-sm;
      background: none;
      color: $text-muted;
      cursor: pointer;

      &:hover {
        color: $text-color;
      }

      &:focus-visible {
        outline: 0;
        box-shadow: $focus-ring;
      }
    }
  }

  .toast-enter-active {
    transition:
      opacity $transition-base,
      transform $transition-base;
  }

  .toast-leave-active {
    transition:
      opacity 0.3s ease-out,
      transform 0.3s ease-out;
  }

  .toast-enter {
    opacity: 0;
    transform: translateY($space-2);
  }

  .toast-leave-to {
    opacity: 0;
    transform: translateX($space-4);
  }

  @media (prefers-reduced-motion: reduce) {
    .toast-enter-active,
    .toast-leave-active {
      transition: opacity $transition-base;
    }

    .toast-enter,
    .toast-leave-to {
      transform: none;
    }
  }
</style>
